<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import FilterPanel from '@/browse/FilterPanel.vue'
import FilterSection from '@/browse/FilterSection.vue'
import SelectFacet from '@/browse/SelectFacet.vue'
import GradeFacet from '@/browse/GradeFacet.vue'
import RadioFacet from '@/browse/RadioFacet.vue'
import SearchInput from '@/components/SearchInput.vue'
import LevelOfSupportSlider from '@/browse/goals/LevelOfSupportSlider.vue'
import StandardSelector from '@/browse/goals/StandardSelector.vue'
import { enteredKeyword, facetOptions } from '@/browse/browse_state'
import type { BrowseState, FacetOption } from '@/browse/browse_state'
import type { FacetCounts } from '@/browse/browse_search'
import { gradeSources } from '@/browse/grades'
import { offeredCategories, offeredGrades, offeredSubjects } from '@/browse/goals/goal_browse'
import { characteristicLabelForUuid } from '@/browse/goals/goal_characteristics'
import type { GoalCharacteristic } from '@/browse/goals/goal_characteristics'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { isSubjectDomainLocked, isSubjectGradeLocked, isSubjectLocked } from '@/contentprofile/premium_locks'
import type { ContentProfileSubject } from '@/contentprofile/premium_locks'
import type { StandardSummary } from '@/standards/api'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'

const KEYWORD_DEBOUNCE_MS = 800
const MAX_EVENT_KEYWORD_LENGTH = 120
const KEYWORD_WARNING_ALERT_CLASSES = '[&_[role=status]]:mb-5 [&_[role=status]]:mt-[5px] [&_[role=status]]:rounded-xs [&_[role=status]]:border [&_[role=status]]:border-[#faebcc] [&_[role=status]]:bg-[#fcf8e3] [&_[role=status]]:p-[15px] [&_[role=status]]:text-[#524633]'

// The change handlers are props rather than emits so each event waits for the page to apply
// the change: the page's filter context, which every event carries, must describe the result.
const props = defineProps<{
  state: BrowseState
  facetCounts: FacetCounts
  characteristics: GoalCharacteristic[] | null
  standards: StandardSummary[]
  isLoadingStandards: boolean
  isSearching: boolean
  onRefine: (key: string, values: string[]) => Promise<unknown>
  onToggle: (key: string, value: string) => Promise<unknown>
  onKeyword: (keyword: string) => Promise<unknown>
}>()

const analytics = getAnalyticsTracker()
const { publishedSubjects } = storeToRefs(useContentProfileStore())
const profileSubjects = computed(() => publishedSubjects.value as ContentProfileSubject[])

const refinements = computed(() => props.state.refinements)
const selectedSubject = computed(() => refinements.value.subject[0] ?? '')
const isCharacteristicSelected = computed(() => Boolean(refinements.value.characteristics[0]))
const isGradeFacetAvailable = computed(() => Boolean(selectedSubject.value || props.state.keyword))

// Counts left undefined enable every option.
const ENABLE_ALL_OPTIONS = undefined

// An empty count for a facet leaves every option enabled rather than none.
function countsOrEnableAll(counts: Record<string, number> | undefined): Record<string, number> | undefined {
  const hasCounts = Boolean(counts && Object.keys(counts).length)
  if (!hasCounts) {
    return ENABLE_ALL_OPTIONS
  }
  return counts
}

const subjectCounts = computed(() => (isCharacteristicSelected.value ? props.facetCounts.subject : ENABLE_ALL_OPTIONS))
const subjectOptions = computed(() => facetOptions(
  offeredSubjects(profileSubjects.value).map((subject) => ({ value: subject, isLocked: isSubjectLocked(profileSubjects.value, subject) })),
  subjectCounts.value,
  refinements.value.subject
))

const gradeSourcesForSubject = computed(() => {
  const grades = offeredGrades(profileSubjects.value, selectedSubject.value)
  return gradeSources(grades).map((source) => ({
    ...source,
    isLocked: isSubjectGradeLocked(profileSubjects.value, selectedSubject.value, source.value)
  }))
})

// Grades stay unavailable until a subject or keyword is chosen.
function disableUnlessGradeFacetAvailable(option: FacetOption): FacetOption {
  if (option.isSelected || isGradeFacetAvailable.value) {
    return option
  }
  return { ...option, isEnabled: false }
}

const gradeOptions = computed(() => facetOptions(
  gradeSourcesForSubject.value,
  countsOrEnableAll(props.facetCounts.grades),
  refinements.value.grades
).map(disableUnlessGradeFacetAvailable))

const categoryOptions = computed(() => facetOptions(
  offeredCategories(profileSubjects.value, selectedSubject.value).map((category) => ({
    value: category,
    isLocked: isSubjectDomainLocked(profileSubjects.value, selectedSubject.value, category)
  })),
  countsOrEnableAll(props.facetCounts.subjectDomain),
  refinements.value.subjectDomain
))

const characteristicCounts = computed(() => (selectedSubject.value ? props.facetCounts.characteristics : ENABLE_ALL_OPTIONS))
const characteristicOptions = computed(() => facetOptions(
  (props.characteristics ?? []).map(({ Uuid, Label }) => ({ value: Uuid, label: Label })),
  characteristicCounts.value,
  refinements.value.characteristics
))

const categoriesUnavailableText = computed(() => {
  if (!selectedSubject.value) {
    return 'Select a subject'
  }
  if (!categoryOptions.value.length) {
    return 'No applicable categories'
  }
  return undefined
})
const standardsUnavailableText = computed(() => {
  if (!selectedSubject.value) {
    return 'Select a subject'
  }
  return undefined
})

async function selectSubject(value: string) {
  await props.onRefine('subject', [value])
  analytics.sendEvent(EVENT_NAMES.BROWSE_GOALS.SELECTED_SUBJECT, { browse_goals_selected_subject: value })
}

async function toggleGrade(grade: string) {
  const isGradeSelected = refinements.value.grades.includes(grade)
  await props.onToggle('grades', grade)
  if (isGradeSelected) {
    analytics.sendEvent(EVENT_NAMES.BROWSE_GOALS.UNSELECTED_GRADE, { browse_goals_unselected_grade: grade })
  } else {
    analytics.sendEvent(EVENT_NAMES.BROWSE_GOALS.SELECTED_GRADE, { browse_goals_selected_grade: grade })
  }
}

async function toggleCategory(category: string) {
  const isCategorySelected = refinements.value.subjectDomain.includes(category)
  await props.onToggle('subjectDomain', category)
  if (isCategorySelected) {
    analytics.sendEvent(EVENT_NAMES.BROWSE_GOALS.UNSELECTED_CATEGORY, { browse_goals_unselected_category: category })
  } else {
    analytics.sendEvent(EVENT_NAMES.BROWSE_GOALS.SELECTED_CATEGORY, { browse_goals_selected_category: category })
  }
}

async function changeLevelOfSupport(level: string) {
  await props.onRefine('levelOfSupport', [level])
  analytics.sendEvent(EVENT_NAMES.BROWSE_GOALS.CHANGED_LEVEL_OF_SUPPORT, { browse_goals_filter_level_of_support: Number(level) })
}

async function selectStandard(standard: string) {
  const previouslySelectedStandard = refinements.value.standard[0]
  if (standard) {
    await props.onRefine('standard', [standard])
    analytics.sendEvent(EVENT_NAMES.BROWSE_GOALS.SELECTED_STANDARD, { browse_goals_selected_standard: standard })
    return
  }
  await props.onRefine('standard', [])
  if (previouslySelectedStandard) {
    analytics.sendEvent(EVENT_NAMES.BROWSE_GOALS.UNSELECTED_STANDARD, { browse_goals_unselected_standard: previouslySelectedStandard })
  }
}

let pendingCharacteristic: string | undefined

async function selectCharacteristic(uuid: string) {
  const isAlreadySelected = uuid === refinements.value.characteristics[0]
  if (isAlreadySelected || uuid === pendingCharacteristic) {
    return
  }
  pendingCharacteristic = uuid
  const label = characteristicLabelForUuid(props.characteristics ?? [], uuid)
  try {
    await props.onRefine('characteristics', uuid ? [uuid] : [])
    if (label) {
      analytics.sendEvent(EVENT_NAMES.BROWSE_GOALS.SELECTED_CHARACTERISTIC, { browse_goals_selected_characteristic: label })
    }
  } finally {
    if (pendingCharacteristic === uuid) {
      pendingCharacteristic = undefined
    }
  }
}

async function updateKeyword(typedKeyword: string) {
  const keyword = enteredKeyword(typedKeyword)
  const previousKeyword = props.state.keyword
  await props.onKeyword(keyword)
  if (keyword) {
    analytics.sendEvent(EVENT_NAMES.BROWSE_GOALS.SEARCHED_KEYWORD, { browse_goals_searched_keyword: keyword.slice(0, MAX_EVENT_KEYWORD_LENGTH) })
  } else if (previousKeyword) {
    analytics.sendEvent(EVENT_NAMES.BROWSE_GOALS.CLEARED_KEYWORD, { browse_goals_cleared_keyword: previousKeyword.slice(0, MAX_EVENT_KEYWORD_LENGTH) })
  }
}
</script>

<template>
  <FilterPanel>
    <FilterSection v-slot="{ labelledBy }" heading="Subjects">
      <SelectFacet :options="subjectOptions" :labelled-by="labelledBy" empty-text="Choose a subject" :clearable="false" has-trigger-states @select="selectSubject" />
    </FilterSection>
    <FilterSection v-slot="{ labelledBy }" heading="Grades">
      <GradeFacet :options="gradeOptions" :labelled-by="labelledBy" @toggle="toggleGrade" />
    </FilterSection>
    <FilterSection v-slot="{ labelledBy }" heading="Level of Support">
      <LevelOfSupportSlider :level="refinements.levelOfSupport[0] ?? ''" :labelled-by="labelledBy" @change="changeLevelOfSupport" />
    </FilterSection>
    <FilterSection
      v-slot="{ labelledBy }"
      heading="Categories"
      :unavailable-text="categoriesUnavailableText"
    >
      <RadioFacet :options="categoryOptions" :labelled-by="labelledBy" hides-unavailable-radios @toggle="toggleCategory" />
    </FilterSection>
    <FilterSection v-slot="{ labelledBy }" heading="Standards" :unavailable-text="standardsUnavailableText">
      <StandardSelector
        :standards="standards"
        :selected-standard-long-ref="refinements.standard[0] ?? ''"
        :is-loading="isLoadingStandards"
        :labelled-by="labelledBy"
        @select="selectStandard"
      />
    </FilterSection>
  </FilterPanel>

  <FilterPanel>
    <FilterSection v-slot="{ labelledBy }" heading="Student Characteristics">
      <SelectFacet :options="characteristicOptions" :labelled-by="labelledBy" empty-text="Filter by characteristic" has-trigger-states @select="selectCharacteristic" />
    </FilterSection>
  </FilterPanel>

  <FilterPanel>
    <SearchInput
      class="[&_label]:text-text-md [&_label]:uppercase"
      :class="KEYWORD_WARNING_ALERT_CLASSES"
      :query="state.keyword"
      :is-searching="isSearching"
      label-text="Search"
      hide-button
      byte-limit-enforced
      :debounce-duration-ms="KEYWORD_DEBOUNCE_MS"
      input-id="browse-search-input"
      @query:changed="updateKeyword"
    />
  </FilterPanel>
</template>
